import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { DsButton, DsModal, DsSegmented } from '@/modules/internal-v2/ds';
import { usePermissions } from '@/hooks/use-permissions';
import { useBulkAddContactsToContactList } from '@/api/contact-list/contact-list.hooks';
import type {
  BulkAddScope,
  BulkAddSource,
  ContactListKind,
} from '@/api/contact-list/contact-list.types';

interface VerteilerImportModalProps {
  listId: number;
  kind: ContactListKind;
  onClose: () => void;
}

/**
 * "Kontakte importieren" dialog for the Verteiler detail. Replaces the former
 * inline "Eigene/Alle Standardkontakte" buttons with one unified flow:
 *
 *  - **Bereich** (scope): own sales-person book vs. every BP (ALL-scope only).
 *  - **Quelle** (source): each BP's default contact vs. all contacts.
 *  - **Filter**: optionally keep only contacts that consented to mail reception
 *    (an enabled stocklist subscription). Stocklist consent is customer-facing,
 *    so the filter is offered only on customer-kind lists.
 *
 * Posts to `POST /v1/contact-lists/:listId/bulk-add-contacts`; the add is
 * idempotent and reports how many new members were inserted.
 */
export function VerteilerImportModal({
  listId,
  kind,
  onClose,
}: VerteilerImportModalProps) {
  const { businessPartnerScope } = usePermissions();
  const canBulkAddAll = businessPartnerScope === 'ALL';

  const [scope, setScope] = useState<BulkAddScope>('own');
  const [source, setSource] = useState<BulkAddSource>('default');
  const [onlyConsenting, setOnlyConsenting] = useState(false);

  const { mutate: bulkAdd, isPending } = useBulkAddContactsToContactList();
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = () => {
    bulkAdd(
      {
        listId,
        body: { source, scope, onlyConsenting: kind === 'customer' && onlyConsenting },
      },
      {
        onSuccess: ({ addedCount }) => {
          enqueueSnackbar(
            addedCount === 0
              ? 'Keine neuen Kontakte — alle passenden sind bereits im Verteiler.'
              : `${addedCount} ${addedCount === 1 ? 'Kontakt' : 'Kontakte'} hinzugefügt.`,
            { variant: addedCount === 0 ? 'info' : 'success' },
          );
          onClose();
        },
        onError: () =>
          enqueueSnackbar('Kontakte konnten nicht hinzugefügt werden.', {
            variant: 'error',
          }),
      },
    );
  };

  const scopeOptions: { value: BulkAddScope; label: string }[] = canBulkAddAll
    ? [
        { value: 'own', label: 'Eigene' },
        { value: 'all', label: 'Alle' },
      ]
    : [{ value: 'own', label: 'Eigene' }];

  const sourceOptions: { value: BulkAddSource; label: string }[] = [
    { value: 'default', label: 'Standardkontakte' },
    { value: 'all', label: 'Alle Kontakte' },
  ];

  return (
    <DsModal
      open
      onClose={onClose}
      title="Kontakte importieren"
      description="Füge passende Kontakte in einem Schritt zu diesem Verteiler hinzu."
      footer={
        <>
          <DsButton variant="ghost" onClick={onClose} disabled={isPending}>
            Abbrechen
          </DsButton>
          <DsButton
            variant="primary"
            onClick={handleSubmit}
            disabled={isPending}
          >
            {isPending ? 'Hinzufügen…' : 'Hinzufügen'}
          </DsButton>
        </>
      }
    >
      <div className="vt-import-form">
        <div className="vt-import-field">
          <span className="vt-import-label">Bereich</span>
          <DsSegmented
            size="small"
            ariaLabel="Bereich"
            value={scope}
            onChange={(v) => setScope(v)}
            options={scopeOptions}
          />
          <span className="vt-import-hint">
            {scope === 'own'
              ? 'Nur deine Geschäftspartner.'
              : 'Alle Geschäftspartner (alle Vertriebsmitarbeiter).'}
          </span>
        </div>

        <div className="vt-import-field">
          <span className="vt-import-label">Quelle</span>
          <DsSegmented
            size="small"
            ariaLabel="Quelle"
            value={source}
            onChange={(v) => setSource(v)}
            options={sourceOptions}
          />
          <span className="vt-import-hint">
            {source === 'default'
              ? 'Der hinterlegte Hauptansprechpartner je Geschäftspartner.'
              : 'Jeder Ansprechpartner der passenden Geschäftspartner.'}
          </span>
        </div>

        {kind === 'customer' && (
          <label className="vt-import-check">
            <input
              type="checkbox"
              checked={onlyConsenting}
              onChange={(e) => setOnlyConsenting(e.target.checked)}
            />
            <span>
              Nur mit Mailempfang-Einwilligung (Bestandsliste)
            </span>
          </label>
        )}
      </div>
    </DsModal>
  );
}
